<script lang="ts">
  // Tab 2 — Conditions & care history. Everything on this tab is derived by the
  // tracking engine: each condition's measures (with target band, attention line,
  // era shading and medication markers), its treatment courses, and a merged care
  // history (medication changes · hypoglycaemia · screening · goal milestones).
  import type { Dataset } from '../types.ts';
  import { STATUS_LABEL, type ConditionTrack, type MeasureTrack } from '../tracking/collate.ts';
  import { fmtDate, fmtMonth, fmtNum, fmtDelta, statusTone, SERIES, ms } from '../format.ts';
  import LineChart from '../charts/LineChart.svelte';
  import CourseTimeline from '../charts/CourseTimeline.svelte';

  let { ds, tracks, selectedId, onSelect }: { ds: Dataset; tracks: ConditionTrack[]; selectedId: string; onSelect: (id: string) => void } = $props();
  const sel = $derived(tracks.find((t) => t.spec.id === selectedId) ?? tracks[0]);
  const domain = $derived<[number, number]>([ms(ds.visits[0].date) - 20 * 86400000, ms(ds.visits[ds.visits.length - 1].date) + 60 * 86400000]);
  const eras = $derived(ds.summary.eras.map((e) => ({ start: ms(e.start), end: e.end ? ms(e.end) : null, label: e.label })));

  const band = (m: MeasureTrack) => { const t = m.spec.target; if (!t) return null; return t.kind === 'range' ? { low: t.low, high: t.high, label: `target ${t.label}` } : t.kind === 'below' || t.kind === 'atOrBelow' ? { high: t.value, label: `target ${t.label}` } : { low: t.value, label: `target ${t.label}` }; };
  const attn = (m: MeasureTrack) => (m.spec.attention ? { value: m.spec.attention.value, label: `attention ${m.spec.attention.label}` } : null);
  const markers = $derived(sel ? [
    ...sel.events.map((e) => ({ t: ms(e.date), label: `${e.action === 'start' ? 'Started' : e.action === 'stop' ? 'Stopped' : e.action === 'increase' ? 'Increased' : 'Reduced'} ${e.drug}${e.dose ? ` ${e.dose}` : ''}`, tone: (e.action === 'stop' ? 'warn' : 'info') as 'warn' | 'info' })),
    ...(sel.spec.includeHypoglycemia ? ds.hypoglycemiaEvents.map((h) => ({ t: ms(h.date), label: `Hypoglycaemia ${h.glucose} mg/dL`, tone: 'danger' as const })) : []),
  ] : []);
  const trendWord = (t: string) => (t === 'improving' ? 'Improving' : t === 'worsening' ? 'Worsening' : t === 'stable' ? 'Stable' : 'Too few points');
  const kindIcon: Record<string, string> = { medication: 'Rx', hypoglycemia: '↓', screening: '◉', milestone: '◆' };
</script>

<div class="cond">
  <aside class="list" aria-label="Conditions">
    {#each tracks as t (t.spec.id)}
      <button class:on={sel?.spec.id === t.spec.id} onclick={() => onSelect(t.spec.id)}>
        <span class="dot {statusTone[t.status]}"></span>
        <span class="nm">{t.spec.shortName}</span>
        <span class="st t-foot">{STATUS_LABEL[t.status]}{#if t.primary?.latest} · {fmtNum(t.primary.latest.value, t.primary.spec.decimals)} {t.primary.spec.unit}{/if}</span>
      </button>
    {/each}
  </aside>

  {#if sel}
    <section class="detail">
      <header class="dh">
        <div>
          <span class="cat">{sel.spec.category} · since {fmtMonth(sel.spec.onset)}</span>
          <h1>{sel.spec.name}</h1>
          <p class="desc">{sel.spec.description}</p>
        </div>
        <div class="statusbox {statusTone[sel.status]}">
          <span class="pill {statusTone[sel.status]}">{STATUS_LABEL[sel.status]}</span>
          <span class="why">{sel.statusReason}</span>
          {#if sel.untreated}<span class="untx">No treatment on the current medication list{#if sel.spec.treatmentClasses.length} — would count: {sel.spec.treatmentClasses.join(', ')}{/if}.</span>{/if}
        </div>
      </header>

      <h2>Measures</h2>
      <div class="measures">
        {#each sel.measures as m, i (m.spec.key)}
          <div class="mcard">
            <div class="mstats">
              <div class="mname">{m.spec.label}{#if sel.spec.primary === m.spec.key}<span class="prim">decides status</span>{/if}</div>
              {#if m.latest}
                <div class="mval"><span class="num big">{fmtNum(m.latest.value, m.spec.decimals)}</span><span class="unit">{m.spec.unit}</span>
                  {#if m.atGoal === true}<span class="tag ok">at goal</span>{:else if m.attention}<span class="tag danger">attention</span>{:else if m.atGoal === false}<span class="tag warn">above goal</span>{/if}
                </div>
                <dl>
                  <div><dt>Latest</dt><dd>{fmtDate(m.latest.date)}</dd></div>
                  {#if m.delta != null}<div><dt>vs previous</dt><dd class="num">{fmtDelta(m.delta, m.spec.decimals)} {m.spec.unit}</dd></div>{/if}
                  <div><dt>Trend</dt><dd>{trendWord(m.trend)}</dd></div>
                  {#if m.spec.target}<div><dt>Target</dt><dd>{m.spec.target.label}</dd></div>{/if}
                  {#if m.atGoalPct12m != null}<div><dt>Last 12 months</dt><dd>{m.atGoalPct12m}% of readings at goal</dd></div>{/if}
                  {#if m.consecutiveAtGoal}<div><dt>At goal for</dt><dd>{m.consecutiveAtGoal} consecutive visit{m.consecutiveAtGoal === 1 ? '' : 's'}</dd></div>{/if}
                  {#if m.min && m.max}<div><dt>Range on record</dt><dd class="num">{fmtNum(m.min.value, m.spec.decimals)} ({fmtMonth(m.min.date)}) – {fmtNum(m.max.value, m.spec.decimals)} ({fmtMonth(m.max.date)})</dd></div>{/if}
                  {#if m.firstAtGoal && m.spec.target}<div><dt>First at goal</dt><dd>{fmtMonth(m.firstAtGoal.date)}</dd></div>{/if}
                </dl>
              {:else}<div class="t-foot">No readings.</div>{/if}
            </div>
            <div class="mchart">
              <LineChart series={[{ key: m.spec.key, label: m.spec.label, color: SERIES[i % SERIES.length], points: m.points.map((p) => ({ t: ms(p.date), y: p.value })) }]}
                unit={m.spec.unit} decimals={m.spec.decimals} height={200} targetBand={band(m)} attentionLine={attn(m)} {eras} {markers} {domain} />
            </div>
          </div>
        {/each}
      </div>

      {#if sel.spec.id === 't2dm' && ds.homeGlucose.length}
        <h2>Home glucose by era <span class="t-foot">14-day self-monitoring windows · 70–180 mg/dL in range</span></h2>
        <div class="smbg">
          {#each ds.homeGlucose as w (w.start)}
            <div class="scard">
              <div class="shead"><b>{w.label}</b><span class="t-foot">mean {w.summary.meanGlucose} mg/dL · {w.summary.timeInRangePct}% in range · {w.summary.below70} low · {w.summary.above180} high</span></div>
              <LineChart series={[
                { key: 'f', label: 'Fasting', color: SERIES[0], points: w.days.map((d) => ({ t: ms(d.date), y: d.fasting })) },
                { key: 'b', label: '2 h after breakfast', color: SERIES[1], points: w.days.map((d) => ({ t: ms(d.date), y: d.postBreakfast })) },
                { key: 'd', label: '2 h after dinner', color: SERIES[2], points: w.days.map((d) => ({ t: ms(d.date), y: d.postDinner })) },
              ]} unit="mg/dL" height={170} targetBand={{ low: 70, high: 180, label: 'in range 70–180' }} yMin={50} yMax={300} />
            </div>
          {/each}
        </div>
      {/if}

      <h2>Treatment</h2>
      {#if sel.courses.length}
        <div class="box"><CourseTimeline courses={sel.courses} {domain} {eras} /></div>
      {:else}
        <div class="box untreatedbox">{sel.spec.expectsTreatment ? 'Nothing on record treats this condition.' : 'Managed without medication.'}{#if sel.spec.treatmentClasses.length} Classes the tracker would recognise: {sel.spec.treatmentClasses.join(', ')}.{/if}</div>
      {/if}
      {#if sel.currentTreatment.length}
        <ul class="curr">{#each sel.currentTreatment as m (m.name)}<li><b>{m.name}</b> {m.dose} <span class="t-foot">· {m.class}</span></li>{/each}</ul>
      {/if}

      <h2>Care history <span class="t-foot">{sel.careHistory.length} entries, newest first</span></h2>
      <ol class="hist">
        {#each sel.careHistory as e, i (e.date + e.kind + e.title + i)}
          <li class="ev {e.severity}">
            <span class="evd">{fmtDate(e.date)}</span>
            <span class="evk" title={e.kind}>{kindIcon[e.kind]}</span>
            <div class="evb"><span class="evt">{e.title}</span><span class="evdet">{e.detail}</span></div>
          </li>
        {/each}
      </ol>
    </section>
  {/if}
</div>

<style>
  .cond { display: grid; grid-template-columns: 240px 1fr; gap: var(--space-5); align-items: start; }
  @media (max-width: 860px) { .cond { grid-template-columns: 1fr; } .list { flex-direction: row; overflow-x: auto; } .list button { min-width: 180px; } }
  .list { display: flex; flex-direction: column; gap: 4px; position: sticky; top: var(--space-4); }
  .list button { display: grid; grid-template-columns: 10px 1fr; column-gap: 10px; text-align: left; padding: var(--space-2) var(--space-3); border-radius: var(--radius-md); border: 1px solid transparent; }
  .list button:hover { background: var(--surface); border-color: var(--line); }
  .list button.on { background: var(--surface); border-color: var(--accent); }
  .dot { width: 10px; height: 10px; border-radius: 50%; margin-top: 5px; }
  .dot.danger { background: var(--danger); } .dot.warn { background: var(--warn); } .dot.accent { background: var(--accent); } .dot.ok { background: var(--ok); } .dot.muted { background: var(--line-strong); }
  .nm { font-weight: 700; }
  .st { grid-column: 2; }

  .detail { display: flex; flex-direction: column; gap: var(--space-3); min-width: 0; }
  .dh { display: grid; grid-template-columns: 1fr minmax(260px, 380px); gap: var(--space-5); align-items: start; }
  @media (max-width: 1000px) { .dh { grid-template-columns: 1fr; } }
  .cat { font-size: var(--fs-foot); text-transform: uppercase; letter-spacing: 0.1em; color: var(--text-3); font-weight: 700; }
  h1 { font-size: calc(var(--fs-heading) + 4px); margin: 2px 0 6px; }
  .desc { color: var(--text-2); }
  .statusbox { display: flex; flex-direction: column; gap: 6px; padding: var(--space-3) var(--space-4); border-radius: var(--radius-md); border: 1px solid var(--line); background: var(--surface); }
  .statusbox.danger { border-color: color-mix(in srgb, var(--danger) 40%, transparent); background: var(--danger-soft); }
  .statusbox.warn { border-color: color-mix(in srgb, var(--warn) 40%, transparent); background: var(--warn-soft); }
  .statusbox.ok { border-color: color-mix(in srgb, var(--ok) 40%, transparent); background: var(--ok-soft); }
  .why { color: var(--text); }
  .untx { color: var(--danger); font-weight: 600; font-size: var(--fs-foot); }
  .pill, .tag { font-size: var(--fs-foot); font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; border-radius: var(--radius-pill); padding: 2px 8px; white-space: nowrap; align-self: flex-start; }
  .pill.danger, .tag.danger { color: var(--danger); background: color-mix(in srgb, var(--danger) 14%, white); }
  .pill.warn, .tag.warn { color: var(--warn); background: color-mix(in srgb, var(--warn) 14%, white); }
  .pill.accent { color: var(--accent); background: var(--accent-soft); }
  .pill.ok, .tag.ok { color: var(--ok); background: color-mix(in srgb, var(--ok) 14%, white); }
  .pill.muted { color: var(--text-3); background: var(--surface-2); }
  h2 { margin-top: var(--space-3); display: flex; align-items: baseline; gap: var(--space-3); }

  .measures { display: flex; flex-direction: column; gap: var(--space-3); }
  .mcard { display: grid; grid-template-columns: 250px 1fr; gap: var(--space-4); background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius-md); padding: var(--space-4); }
  @media (max-width: 860px) { .mcard { grid-template-columns: 1fr; } }
  .mname { font-weight: 700; display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
  .prim { font-size: var(--fs-foot); font-weight: 700; color: var(--accent); background: var(--accent-soft); border-radius: var(--radius-pill); padding: 1px 7px; text-transform: none; letter-spacing: 0; }
  .mval { display: flex; align-items: baseline; gap: 6px; margin: 4px 0 6px; flex-wrap: wrap; }
  .big { font-size: 28px; font-weight: 600; line-height: 1; }
  .unit { color: var(--text-3); }
  .mstats dl { display: flex; flex-direction: column; gap: 3px; }
  .mstats dl div { display: grid; grid-template-columns: 96px 1fr; gap: 6px; font-size: var(--fs-foot); }
  .mstats dt { color: var(--text-3); }
  .mstats dd { color: var(--text-2); }
  .mchart { min-width: 0; }

  .smbg { display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); gap: var(--space-3); }
  .scard { background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius-md); padding: var(--space-3) var(--space-4); }
  .shead { display: flex; flex-direction: column; margin-bottom: 4px; }

  .box { background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius-md); padding: var(--space-3) var(--space-4); }
  .untreatedbox { color: var(--text-2); }
  .curr { list-style: none; display: flex; flex-wrap: wrap; gap: var(--space-2); }
  .curr li { background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius-pill); padding: 4px 12px; }

  .hist { list-style: none; display: flex; flex-direction: column; border: 1px solid var(--line); border-radius: var(--radius-md); background: var(--surface); overflow: hidden; }
  .ev { display: grid; grid-template-columns: 110px 26px 1fr; gap: var(--space-3); padding: var(--space-2) var(--space-4); border-bottom: 1px solid var(--line); align-items: start; }
  .ev:last-child { border-bottom: none; }
  .evd { color: var(--text-3); font-size: var(--fs-foot); padding-top: 2px; white-space: nowrap; }
  .evk { font-weight: 800; font-size: var(--fs-foot); text-align: center; border-radius: var(--radius-sm); padding: 2px 0; color: var(--text-3); background: var(--surface-2); }
  .ev.warn .evk { color: var(--warn); background: var(--warn-soft); }
  .ev.danger .evk { color: var(--danger); background: var(--danger-soft); }
  .ev.good .evk { color: var(--ok); background: var(--ok-soft); }
  .evb { display: flex; flex-direction: column; }
  .evt { font-weight: 600; }
  .evdet { color: var(--text-2); font-size: var(--fs-foot); line-height: 1.5; }
  @media (max-width: 700px) { .ev { grid-template-columns: 90px 22px 1fr; padding-left: 12px; padding-right: 12px; } }
</style>
